import React, { useState } from "react";
import { type GetStaticPaths, type GetStaticProps, type NextPage } from "next";
import { ArticleJsonLd } from "next-seo";
import { Text, Flex, Box, SimpleGrid } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { groq } from "next-sanity";
import { MDXRemote, type MDXRemoteSerializeResult } from "next-mdx-remote";
import { getClient, getSqlPage, getAnnouncementBanner, type Banner, type SqlPage } from "lib/sanity";
import { components } from "components/mdx/components";
import { Page } from "components/page";
import { Article, ArticleContainer } from "components/article";
import { ResourceCard } from "components/resource-card";
import { DemoEmbedFull } from "components/demo-embed";
import { ArrowLink } from "ui/link";
import { getPageMetaImage } from "utils";
import NotFound from "pages/404";
import { Sidebar, type SidebarHeading } from "components/sidebar";
import { getArticleHeadings, serializeArticle } from "components/mdx/utils";
import { HubSpotForm } from "components/hubspot";
import { identifyUser, trackEvent } from "lib/analytics";
import { BookDemoButton } from "components/event-tracking";
import { Chip } from "ui/chips";
import { Section } from "components/section";

type PageProps = {
  announcementBanner?: Banner;
  post: SqlPage;
  slug: string;
  mdx: MDXRemoteSerializeResult;
  headings: SidebarHeading[];
};

const hubspotFields = ["firstname", "lastname", "company"];
const hubspotFormId = "80c28a9e-255a-4ee2-a52d-77597b2a5a7f";

const navatticDemoId = process.env.NEXT_PUBLIC_NAVATTIC_UNGATED_SQL;
const navatticDemoName = "Ungated SQL dictionary";

const SqlDictionaryPage: NextPage<Readonly<PageProps>> = ({ post, slug, mdx, headings, announcementBanner }) => {
  const router = useRouter();
  const [success, setSuccess] = useState(false);

  if (!router.isFallback && (!slug || !post)) {
    return <NotFound />;
  }

  if (!post) {
    return null;
  }

  const onFormSubmit = (lead: Record<string, string>) => {
    trackEvent("Newsletter Signup", {
      Type: "SQL data tips",
      Email: lead.email,
      "First Name": lead.firstname,
      "Last Name": lead.lastname,
      page: window.location.href,
    });
    identifyUser({
      email: lead.email,
      firstName: lead.firstname,
      lastName: lead.lastName,
    });
    setSuccess(true);
  };

  const pageTitle = post.metaTitle ?? post.name;
  const pageDescription = post.metaDescription ?? post.name;

  return (
    <Page
      hasSmoothScroll
      title={pageTitle}
      description={pageDescription}
      image={getPageMetaImage()}
      announcementBanner={announcementBanner}
      footerOptions={{
        ctaProps: {
          title: "Activate your data in less than 5 minutes",
          description: "",
          actions: <BookDemoButton />,
        },
      }}
    >
      <Section as="article" mt={{ base: 4, md: 6 }} mb={{ base: "biggest", md: "huge" }}>
        <ArrowLink href="/sql-dictionary" direction="back" variant="dark">
          Back to SQL Dictionary
        </ArrowLink>

        <Flex gap={16} direction={{ base: "column", lg: "row" }} align="start" mt={{ base: 8, md: 12 }}>
          <Flex
            w="100%"
            align="start"
            borderBottom="1px"
            borderColor="base.border"
            direction="column"
            gap={5}
            flex={1}
            pb={{ base: 12, md: 16 }}
            mb={{ base: 12, md: 16 }}
          >
            <Chip variant="blue">{post.category.name}</Chip>
            <Text as="h1" textStyle="h2">
              {post.name}
            </Text>

            <ArticleJsonLd
              type="Blog"
              url={`https://hightouch.com/sql-dictionary/${post.slug}`}
              images={["https://hightouch.com/assets/meta.png"]}
              title={pageTitle}
              datePublished={post.createdAt}
              dateModified={post.updatedAt}
              authorName="Hightouch"
              description={pageDescription}
            />
          </Flex>
        </Flex>

        <ArticleContainer>
          <Article>
            <MDXRemote lazy components={components({ type: "sqlPage" })} {...mdx} />

            <Box>
              <Text as="h2" textStyle="h4" mt={16}>
                Related
              </Text>

              <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap={4} mt={9}>
                {post.related.map(({ slug, name }) => (
                  <ResourceCard
                    key={slug}
                    href={`/sql-dictionary/${slug}`}
                    title={name}
                    titleElement="h3"
                    actionLabel="Read article"
                  />
                ))}
              </SimpleGrid>
            </Box>
          </Article>

          <Sidebar
            headings={headings}
            path={`sql-dictionary/${slug}`}
            cta={
              <Box background="brand.backgroundSecondary.light" borderRadius="base" p="small">
                <Text mb="smaller" textStyle="body3" fontWeight="semibold">
                  Subscribe to monthly data tips
                </Text>

                <HubSpotForm
                  label="Subscribe"
                  displayedFields={hubspotFields}
                  formId={hubspotFormId}
                  successContent={success && <Text textStyle="caption1">Thank you for subscribing!</Text>}
                  onSubmit={onFormSubmit}
                />
              </Box>
            }
          />
        </ArticleContainer>
      </Section>

      <DemoEmbedFull
        pt={24}
        demoId={navatticDemoId}
        demoName={navatticDemoName}
        title="Ready to put your SQL knowledge to work?"
        description="Practice writing SQL to call data from the warehouse and sync it into Google Sheets in this 5 minute interactive demo."
      />
    </Page>
  );
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ params, preview = false }) => {
  const slug = params?.slug ? String(params?.slug) : "";
  const [post, announcementBanner] = await Promise.all([getSqlPage(slug), getAnnouncementBanner()]);

  if (!post) {
    return {
      notFound: true,
    };
  }

  const mdx = await serializeArticle(post.content);

  return {
    props: {
      preview,
      slug,
      post,
      mdx,
      headings: getArticleHeadings(post.content),
      announcementBanner,
    },
    revalidate: 60,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs: Array<{ slug: string }> = await getClient(false).fetch(groq`*[_type == "sqlPage"] { slug }`);

  return {
    paths: slugs.map(({ slug }: { slug: string }) => ({ params: { slug } })),
    fallback: "blocking",
  };
};

export default SqlDictionaryPage;
